'use client';

/**
 * AgentNarrativeCard — the agent's DECIDE phase made visible.
 *
 * When ki_ask dispatches a synthesis-bearing capability (today:
 * obeya_project depth=synthesis; future: data_quality, vendor_evaluation,
 * etc.), the response payload carries:
 *
 *   payload.narrative                  — summary + urgency + recommendations
 *   payload.synthesis_metadata         — inner CV, trust_level, template
 *   payload.project.rule_audit         — matched rule + alternatives
 *   picked_capability + score          — which capability the agent chose
 *   simulation_id                      — parent CV for Glass Box drill-in
 *
 * This card stacks ABOVE the dispatched capability's renderer_card
 * (`obeya_health` for obeya_project). Together they're the agent's
 * OODA-L visible: DECIDE here (what the agent picked + how it framed it),
 * ACT below (the data the capability returned).
 *
 * Frontend-only — substrate is already complete. No new BFFs.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { AlertTriangle, CheckCircle, ChevronRight, Clock, Lightbulb, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useCardTypography } from '@/lib/design/card-chrome';
import { useDemoAskStore, type AskNarrative, type AskSynthesisMetadata } from '@/lib/stores/demo-ask-store';
import { useTurn } from '@/lib/stores/turn-context';

function urgencyIcon(u?: string) {
  if (u === 'low') return <CheckCircle className="h-4 w-4 text-green-400" />;
  if (u === 'high') return <AlertTriangle className="h-4 w-4 text-red-400" />;
  return <Clock className="h-4 w-4 text-amber-400" />;
}

function priorityColor(p?: string) {
  if (p === 'high') return 'bg-red-500/10 border-red-500/20 text-red-400';
  if (p === 'medium') return 'bg-amber-500/10 border-amber-500/20 text-amber-400';
  if (p === 'low') return 'bg-green-500/10 border-green-500/20 text-green-400';
  return 'bg-muted border-border';
}

function urgencyBg(u?: string) {
  if (u === 'low') return 'bg-green-500/10 border-green-500/20';
  if (u === 'medium') return 'bg-amber-500/10 border-amber-500/20';
  if (u === 'high') return 'bg-red-500/10 border-red-500/20';
  return 'bg-muted border-border';
}

export function AgentNarrativeCard() {
  const T = useCardTypography();
  // Per-turn context (transcript mount) wins over store-latest. Without
  // this, every narrative card in a stacked transcript would display the
  // *latest* turn's data. When mounted outside the transcript (no
  // provider), fall back to the store.
  const turn = useTurn();
  const storeResponse = useDemoAskStore((s) => s.response);
  const response = turn?.response ?? storeResponse;

  if (!response) return null;

  const payload = (response.payload ?? null) as null | {
    narrative?: AskNarrative;
    synthesis_metadata?: AskSynthesisMetadata;
    project?: {
      rule_audit?: { matched_rule?: string; label?: string };
    };
  };
  // Doctrine: ki_ask's outer always-synthesize wrap sets `response.
  // narrative` (a plain string) and `response.key_findings` (list of
  // bullets). Inline-synthesis capabilities (project_health,
  // project_data_quality) ALSO nest a richer object under
  // `payload.narrative` with urgency + recommendations. Both can be
  // present; prefer the structured object when available (richer
  // render), fall back to the outer string. Without this, capabilities
  // that lean on the outer wrap only (project_rfqs, pipeline_summary,
  // project_perf — no inline synthesis) had no path to render their
  // narrative because the old read only looked at the inline-shape.
  const inlineNarrative = payload?.narrative;
  const outerSummary = response.narrative ?? undefined;
  const outerFindings = response.key_findings ?? [];
  // Choose the richest available source for the urgency badge +
  // summary text. Recommendations come from the inline shape only;
  // outer-wrap responses don't have them.
  const summary = inlineNarrative?.summary ?? outerSummary;
  const urgency = inlineNarrative?.urgency;
  const recommendations = inlineNarrative?.recommendations ?? [];
  const issues = inlineNarrative?.issues ?? outerFindings;
  const meta = payload?.synthesis_metadata;
  const matchedRule = payload?.project?.rule_audit?.matched_rule;

  // No-op when no narrative at all. The slot's hasNarrative gate
  // already short-circuits this in the common case; defensive here
  // for non-transcript mounts.
  if (!summary) return null;

  return (
    <article className="rounded-md border border-border bg-card shadow-sm overflow-hidden">
      <header className="flex items-center gap-2 px-3 py-2 border-b border-border/60">
        <Sparkles className="w-3.5 h-3.5 text-violet-400" />
        <h3 className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
          Agent decision
        </h3>
        {response.picked_capability && (
          <div className="ml-auto flex items-center gap-2">
            <span className={cn(T.metadata, 'font-mono text-muted-foreground text-[10px]')}>
              {response.picked_capability}
            </span>
            {typeof response.capability_score === 'number' && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-muted/60 text-muted-foreground tabular-nums">
                {response.capability_score.toFixed(3)}
              </span>
            )}
          </div>
        )}
      </header>

      <div className="p-3 space-y-3">
        {/* Summary + urgency badge (urgency only shown when present —
            outer-wrap narratives don't carry it) */}
        <div className={cn('flex items-start gap-2 p-2 rounded-md border', urgencyBg(urgency))}>
          {urgencyIcon(urgency)}
          <div className="min-w-0 flex-1">
            {urgency && (
              <p className="text-[11px] font-medium capitalize">{urgency} urgency</p>
            )}
            <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed">
              {summary}
            </p>
          </div>
        </div>

        {/* Cited rule — pulled from rule_audit so the user can see the
            grounded rationale, not just LLM prose. */}
        {matchedRule && (
          <div className="text-[10px] text-muted-foreground flex items-center gap-1.5">
            <ChevronRight className="h-3 w-3" />
            <span>Matched rule:</span>
            <span className="font-mono text-foreground">{matchedRule}</span>
          </div>
        )}

        {/* Key findings (outer wrap) OR Recommendations (inline). The
            inline-synthesis shape carries richer per-item objects with
            priority + based_on; the outer wrap is a flat string list.
            Render whichever is present. */}
        {recommendations.length > 0 ? (
          <div className="space-y-1.5">
            <p className={cn(T.metadata, 'text-muted-foreground text-[10px] uppercase tracking-wide')}>
              Recommendations
            </p>
            {recommendations.map((rec, i) => (
              <div key={i} className={cn('rounded-md border px-2 py-1.5', priorityColor(rec.priority))}>
                <div className="flex items-start gap-1.5">
                  <Lightbulb className="h-3 w-3 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium leading-snug">{rec.action}</p>
                    {rec.based_on && (
                      <p className="text-[9px] text-muted-foreground mt-0.5 flex items-center gap-1">
                        <ChevronRight className="h-2.5 w-2.5" />
                        Based on: {rec.based_on}
                      </p>
                    )}
                  </div>
                  {rec.priority && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-background/50 capitalize shrink-0">
                      {rec.priority}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : issues.length > 0 ? (
          <div className="space-y-1.5">
            <p className={cn(T.metadata, 'text-muted-foreground text-[10px] uppercase tracking-wide')}>
              Key findings
            </p>
            {issues.map((item, i) => (
              <div key={i} className="rounded-md border border-border bg-muted/30 px-2 py-1.5">
                <div className="flex items-start gap-1.5">
                  <Lightbulb className="h-3 w-3 shrink-0 mt-0.5 text-muted-foreground" />
                  <p className="text-[11px] leading-snug">{item}</p>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Audit provenance — parent CV (simulation_id) + inner CV
            (synthesis_metadata.conversation_id) + trust. Mono-formatted
            so users can copy-paste into Glass Box / cypher. */}
        <div className="text-[9px] text-muted-foreground pt-2 border-t border-border/40 space-y-0.5">
          {response.simulation_id && (
            <div className="flex items-center gap-1.5">
              <span>Parent CV:</span>
              <span className="font-mono text-foreground/70">{response.simulation_id}</span>
            </div>
          )}
          {meta?.conversation_id && (
            <div className="flex items-center gap-1.5">
              <span>Synthesis CV:</span>
              <span className="font-mono text-foreground/70">{meta.conversation_id}</span>
            </div>
          )}
          {meta?.trust_level && (
            <div className="flex items-center gap-1.5">
              <span>Trust:</span>
              <span className="capitalize text-foreground/70">{meta.trust_level}</span>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
